import Button from "@/components/ui/Button";
import TextField from "@/components/ui/Textfield";
import React, { useState } from "react";
import Dropdown from "@/components/ui/DropDown";
import useGlobalStore from "@/store/globalStore";
import { useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { timezone } from "@/helper/options";
import SearchableDropdown from "@/components/ui/SearchDropDown";
import useMediaUserOnboardingStore from "../store/mediaUserOnboardingStore";

interface Props {
  onNext: () => void;
}

const StepOneMediaUserOnboarding: React.FC<Props> = ({ onNext }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { sendUserData } = useMediaUserOnboardingStore(
    useShallow((state) => ({
      sendUserData: state.sendUserData,
    }))
  );
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    password: "",
    password_confirmation: "",
    number: "",
    country_code: "+1",
    address: "",
    date_of_birth: "",
    language: "",
    timezone: "",
    bio: "",
  });

  const setError = useGlobalStore((state) => state.setError);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const {
      first_name,
      last_name,
      password,
      password_confirmation,
      number,
      country_code,
      address,
      date_of_birth,
      language,
      timezone,
      bio,
    } = formData;

    if (!first_name.trim()) return setError("First Name is required.");
    if (!last_name.trim()) return setError("Last Name is required.");
    if (!password.trim()) return setError("Password is required.");
    if (!password_confirmation.trim())
      return setError("Confirm password is required.");
    if (!number.trim()) return setError("Phone number is required.");
    if (!country_code.trim()) return setError("Country code is required.");
    if (!address.trim()) return setError("Address is required.");
    if (!date_of_birth.trim()) return setError("Date of birth is required.");
    if (!language.trim()) return setError("Language is required.");
    if (!timezone.trim()) return setError("Timezone is required.");
    if (!bio.trim()) return setError("Bio is required.");

    if (password !== password_confirmation) {
      return setError("Passwords do not match.");
    }

    setIsLoading(true);

    try {
      await sendUserData({
        first_name,
        last_name,
        password,
        password_confirmation,
        number,
        country_code,
        address,
        date_of_birth,
        language,
        timezone,
        bio,
        token: searchParams.get("token"),
      });

      onNext();
      setIsLoading(false);
    } catch (err: any) {
      setIsLoading(false);
      setError(err?.message || "Failed to submit user data.");
    }
  };

  return (
    <div className="bg-[#F8F8F8] flex flex-col items-center justify-center px-4 py-14">
      <div className="max-w-md w-full text-center mb-6">
        <h1 className="text-5xl font-bold text-[#EA33C0] mb-4">MediaBridge</h1>
        <h2 className="text-xl font-semibold mb-2">Welcome, there!</h2>
        <p className="text-sm text-gray-600">
          You’ve been appointed Media Admin for your team. Congrats! <br />
          Please fill in the required fields so we can finish setting up your
          account.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="w-full max-w-xl space-y-4">
        <div className="flex items-center font-semibold">
          <div className="mr-2 w-56">First Name</div>
          <TextField
            type="text"
            name="first_name"
            value={formData.first_name}
            onChange={onChange}
            placeholder="First Name"
            className="w-full"
          />
        </div>

        <div className="flex items-center font-semibold">
          <div className="mr-2 w-56">Last Name</div>
          <TextField
            type="text"
            name="last_name"
            value={formData.last_name}
            onChange={onChange}
            placeholder="Last Name"
            className="w-full"
          />
        </div>

        <div className="flex items-center font-semibold">
          <div className="mr-2 w-56">Phone Number</div>
          <div className={`flex gap-2 items-center w-full`}>
            <div className="w-32">
              <Dropdown
                selected={formData.country_code}
                setSelected={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    country_code: e.toString(),
                  }));
                }}
                options={[
                  {
                    title: "+1",
                    value: "+1",
                  },
                ]}
                className="w-full"
              />
            </div>
            <TextField
              type="tel"
              value={formData.number}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, number: e.target.value }))
              }
              placeholder="Phone number"
              className="w-full"
            />
          </div>
        </div>

        <div className="flex items-center font-semibold">
          <div className="mr-2 w-56">Address</div>
          <TextField
            type="text"
            name="address"
            value={formData.address}
            onChange={onChange}
            placeholder="Address"
            className="w-full"
          />
        </div>

        <div className="flex items-center font-semibold">
          <div className="mr-2 w-56">Date of Birth</div>
          <TextField
            type="date"
            name="date_of_birth"
            value={formData.date_of_birth}
            onChange={onChange}
            placeholder="Date of Birth"
            className="w-full"
          />
        </div>

        <div className="flex items-center font-semibold">
          <div className="mr-2 w-56">Language</div>
          <SearchableDropdown
            selected={formData.language}
            setSelected={(e) => {
              setFormData((prev) => ({
                ...prev,
                language: e.toString(),
              }));
            }}
            options={[{ title: "English", value: "en" }]}
            className="w-full"
          />
        </div>

        <div className="flex items-center font-semibold">
          <div className="mr-2 w-56">Timezone</div>
          <SearchableDropdown
            selected={formData.timezone}
            setSelected={(e) => {
              setFormData((prev) => ({
                ...prev,
                timezone: e.toString(),
              }));
            }}
            options={timezone}
            className="w-full"
          />
        </div>

        <div className="flex items-center font-semibold">
          <div className="mr-2 w-56">Bio</div>
          <TextField
            type="text"
            name="bio"
            value={formData.bio}
            onChange={onChange}
            placeholder="Bio"
            className="w-full"
          />
        </div>

        <div className="flex items-center font-semibold">
          <div className="mr-2 w-56">Password</div>
          <TextField
            type="password"
            name="password"
            value={formData.password}
            onChange={onChange}
            placeholder="Password"
            className="w-full"
          />
        </div>

        <div className="flex items-center font-semibold">
          <div className="mr-2 w-56">Confirm Password</div>
          <TextField
            type="password"
            name="password_confirmation"
            value={formData.password_confirmation}
            onChange={onChange}
            placeholder="Confirm Password"
            className="w-full"
          />
        </div>

        <div className="pt-4 text-center flex justify-center">
          <Button type="primary" onClick={handleSubmit} isLoading={isLoading}>
            Save Info
          </Button>
        </div>
      </form>
    </div>
  );
};

export default StepOneMediaUserOnboarding;
